import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { apiClient } from '../../utils/api';
import { colors, spacing, media, borderRadius, typography } from '../../styles/GlobalStyles';
import { LogOut, Settings, Users, Trophy, Volume2, VolumeX, House, BarChart3 } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface LayoutProps {
  children: ReactNode;
}

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #000000;
`;

const Header = styled.header`
  background: rgba(2, 2, 2, 0.96);
  border-bottom: 1px solid #1c1c1c;
  padding: 0.6rem 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  position: sticky;
  top: 0;
  z-index: 40;
  backdrop-filter: blur(8px);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  flex-wrap: wrap;

  ${media.tablet} {
    padding: 0 ${spacing[6]};
    flex-wrap: nowrap;
  }

  ${media.desktop} {
    padding: 0 ${spacing[8]};
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #009025;
  text-decoration: none;

  ${media.tablet} {
    font-size: 1.25rem;
  }
`;

const LogoText = styled.span`
  display: none;

  ${media.tablet} {
    display: inline;
  }
`;

const Nav = styled.nav`
  display: none;
  
  ${media.tablet} {
    display: flex;
    align-items: center;
    gap: ${spacing[2]};
    padding: ${spacing[1]};
    border: 1px solid ${colors.grey[800]};
    border-radius: ${borderRadius.lg};
    background: rgba(255, 255, 255, 0.02);
  }
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid transparent;
  border-radius: ${borderRadius.md};
  color: ${props => props.$isActive ? colors.green : colors.grey[300]};
  background: ${props => props.$isActive ? `${colors.green}10` : 'transparent'};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.white};
    border-color: ${colors.grey[700]};
    background: ${colors.grey[900]};
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};

  ${media.tablet} {
    gap: ${spacing[4]};
  }
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.lg};
  background: rgba(255, 255, 255, 0.02);
  padding: ${spacing[1]};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.md};
  background: rgba(255, 255, 255, 0.02);
  padding: ${spacing[1]} ${spacing[2]};
  
  ${media.tablet} {
    gap: ${spacing[3]};
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: linear-gradient(145deg, ${colors.green}, #44d168);
  color: ${colors.black};
  border-radius: ${borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const Username = styled.span`
  font-weight: 600;
  color: ${colors.white};
  font-size: 0.875rem;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: ${colors.grey[400]};
  text-transform: uppercase;
`;

const ConnectionStatus = styled.div<{ $isConnected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: 0.75rem;
  color: ${props => props.$isConnected ? colors.success : colors.error};
  border: 1px solid ${props => props.$isConnected ? `${colors.success}40` : `${colors.error}40`};
  border-radius: ${borderRadius.full};
  padding: ${spacing[1]} ${spacing[2]};
  background: ${props => props.$isConnected ? `${colors.success}10` : `${colors.error}10`};

  span {
    display: inline;
  }
`;

const StatusDot = styled.div<{ $isConnected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: ${borderRadius.full};
  background: ${props => props.$isConnected ? colors.success : colors.error};
  animation: ${props => props.$isConnected ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${borderRadius.md};
  color: ${colors.grey[300]};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.grey[900]};
    border-color: ${colors.grey[500]};
    color: ${colors.white};
  }
`;

const SoundToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: ${spacing[2]} ${spacing[3]};
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${borderRadius.md};
  color: ${colors.grey[300]};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.grey[900]};
    border-color: ${colors.grey[500]};
    color: ${colors.white};
  }
`;

const DesktopLabel = styled.span`
  display: none;

  ${media.tablet} {
    display: inline;
  }
`;

const Main = styled.main`
  flex: 1;
  padding: ${spacing[4]} 0;
  background: #000000;

  ${media.tablet} {
    padding: ${spacing[6]} 0;
  }
`;

const MobileNav = styled.div`
  display: flex;
  gap: ${spacing[2]};
  width: 100%;
  justify-content: flex-start;
  margin-top: ${spacing[2]};
  overflow-x: auto;
  padding-bottom: ${spacing[1]};
  
  ${media.tablet} {
    display: none;
  }
`;

const MobileNavButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$isActive'
})<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  padding: ${spacing[2]} ${spacing[3]};
  flex: 0 0 64px;
  min-height: 44px;
  background: ${props => props.$isActive ? `${colors.green}20` : 'transparent'};
  color: ${props => props.$isActive ? colors.green : colors.grey[300]};
  border: 1px solid ${colors.grey[600]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isActive ? `${colors.green}20` : colors.grey[800]};
    color: ${props => props.$isActive ? colors.green : colors.white};
  }
`;

const MobileNavLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
`;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { isConnected, activeUsers } = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [chatSoundEnabled, setChatSoundEnabled] = useState(true);

  useEffect(() => {
    const storedPreference = localStorage.getItem('chatSoundEnabled');
    if (storedPreference !== null) {
      setChatSoundEnabled(storedPreference === 'true');
    }
  }, []);

  const toggleChatSound = () => {
    const nextValue = !chatSoundEnabled;
    setChatSoundEnabled(nextValue);
    localStorage.setItem('chatSoundEnabled', String(nextValue));
    toast(nextValue ? 'Chat sound notifications on' : 'Chat sound notifications muted', {
      icon: nextValue ? '🔔' : '🔕',
      duration: 2000,
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/draft') {
      return location.pathname === '/draft' || location.pathname.startsWith('/draft/');
    }

    return location.pathname === path;
  };

  const handleDraftNavigation = async () => {
    try {
      const response = await apiClient.drafts.getAll();
      if (!response.success) {
        toast.error('Unable to load drafts right now.');
        return;
      }

      const drafts = response.data?.drafts || [];
      const activeDraft = drafts.find((draft: any) => draft.status === 'active');
      if (activeDraft?._id) {
        navigate(`/draft/${activeDraft._id}`);
        return;
      }

      const createdDraft = drafts.find((draft: any) => draft.status === 'created');
      if (createdDraft?._id) {
        navigate(`/draft/${createdDraft._id}`);
        toast('Draft is created but not started yet.', { icon: '⏳' });
        return;
      }

      toast.error('No draft available to join right now.');
    } catch (error) {
      console.error('Failed to navigate to draft:', error);
      toast.error('Failed to open draft.');
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: House },
    { path: '/draft', label: 'Draft', icon: Trophy },
    { path: '/teams', label: 'Teams', icon: Users },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    ...(user?.role === 'admin' ? [{ path: '/admin', label: 'Admin', icon: Settings }] : []),
  ];

  return (
    <LayoutContainer>
      <Header>
        <HeaderContent>
          <Logo to="/dashboard">
            <Trophy size={24} />
            <LogoText>Fantasy Draft</LogoText>
          </Logo>

          <Nav>
            {navItems.map(item => (
              <NavLink 
                key={item.path}
                to={item.path} 
                $isActive={isActive(item.path)}
                onClick={(event) => {
                  if (item.path === '/draft') {
                    event.preventDefault();
                    handleDraftNavigation();
                  }
                }}
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            ))}
          </Nav>

          <UserMenu>
            <ConnectionStatus $isConnected={isConnected}>
              <StatusDot $isConnected={isConnected} />
              <span>
                {isConnected ? 'Connected' : 'Disconnected'}
                {isConnected && activeUsers.length > 0 && ` (${activeUsers.length})`}
              </span>
            </ConnectionStatus>

            <UserActions>
              <SoundToggleButton onClick={toggleChatSound} title={chatSoundEnabled ? 'Mute chat sounds' : 'Enable chat sounds'}>
                {chatSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                <DesktopLabel>{chatSoundEnabled ? 'Sound On' : 'Muted'}</DesktopLabel>
              </SoundToggleButton>

              <UserInfo>
                <UserAvatar>
                  {user?.username.charAt(0).toUpperCase()}
                </UserAvatar>
                <UserDetails>
                  <Username>{user?.username}</Username>
                  <UserRole>{user?.role}</UserRole>
                </UserDetails>
              </UserInfo>

              <LogoutButton onClick={handleLogout}>
                <LogOut size={16} />
                <DesktopLabel>Logout</DesktopLabel>
              </LogoutButton>
            </UserActions>
          </UserMenu>

          <MobileNav>
            <SoundToggleButton onClick={toggleChatSound} title={chatSoundEnabled ? 'Mute chat sounds' : 'Enable chat sounds'}>
              {chatSoundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </SoundToggleButton>
            {navItems.map(item => (
              <MobileNavButton
                key={item.path}
                $isActive={isActive(item.path)}
                onClick={() => {
                  if (item.path === '/draft') {
                    handleDraftNavigation();
                    return;
                  }

                  navigate(item.path);
                }}
              >
                <item.icon size={18} />
                <MobileNavLabel>{item.label}</MobileNavLabel>
              </MobileNavButton>
            ))}
          </MobileNav>
        </HeaderContent>
      </Header>

      <Main>
        {children}
      </Main>
    </LayoutContainer>
  );
};

export default Layout;
